import React, { useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  DialogOverlay,
  DialogOverlayFooter,
  DialogOverlayActions,
  Button,
  Paragraph,
  H3,
  Expander,
  ExpanderHeader,
  ExpanderContent,
  Separator,
  Tabs,
  Tab,
} from '@fs/zion-ui'
import { Link } from '@fs/zion-router'
import Cache from '@fs/zion-cache'
import { SocialMenu } from '@fs/zion-social'
import usePrisonAccess from '../../../../../Give/hooks/usePrisonAccess'

function StyledExpander({ content, headerText }) {
  return (
    <>
      <Separator size="sm" />
      <Expander>
        <ExpanderHeader>
          <H3 size="H5" supplementary>
            {headerText}
          </H3>
        </ExpanderHeader>
        <ExpanderContent>{content}</ExpanderContent>
      </Expander>
    </>
  )
}

export default function WelcomeOverlay({ welcomeOverlay }) {
  const [t] = useTranslation()
  const sharableInstructions = {
    url: 'https://www.familysearch.org/en/help/helpcenter/article/welcome-to-family-review',
  }
  const sharableFaq = {
    url: 'https://www.familysearch.org/en/help/helpcenter/article/frequently-asked-questions-about-family-review',
  }
  const { accessFromPrison, isReady } = usePrisonAccess()

  useEffect(() => {
    const cache = new Cache({ storeName: 'RIP_settings' })
    cache.getItem('shownFRWelcome').then((shownWelcome) => {
      if (!shownWelcome) {
        welcomeOverlay.handleClick()
        cache.setItem('shownFRWelcome', true)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/exhaustive-deps -- only needs to be run once
  }, [])

  const steps = [
    {
      key: 'step1',
      headerText: t('family-review.welcome-overlay.step1', 'Step 1: Find the Primary Person'),
      content: (
        <Paragraph size="sm">
          {t(
            'family-review.welcome-overlay.step1-content',
            'Identify the primary person in the record. This is the individual (or individuals, depending on the record type) at the center of the event—the person who was born, for example, or the couple who was married.'
          )}
        </Paragraph>
      ),
    },
    {
      key: 'step2',
      headerText: t('family-review.welcome-overlay.step2', 'Step 2: Review the Name, Sex, and Relationship'),
      content: (
        <>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.step2-content',
              "Verify that the primary person's name, sex, and relationships have been correctly documented in the transcription. If you see a mistake, you can fix it."
            )}
          </Paragraph>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.step2-content2',
              'Verify that the names of the other people mentioned in the record have also been transcribed correctly.'
            )}
          </Paragraph>
          <ul>
            <li>
              {t(
                'family-review.welcome-overlay.step2-item',
                'Choose the relationship that connects them to the primary person.'
              )}
            </li>
            <li>
              <Trans i18nKey="family-review.welcome-overlay.step2-item2">
                If you cannot see the correct relationship type, select <strong>Other</strong>.
              </Trans>
            </li>
          </ul>
        </>
      ),
    },
    {
      key: 'step3',
      headerText: t('family-review.welcome-overlay.step3', 'Step 3: Verify Events'),
      content: (
        <>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.step3-content',
              'Identify the primary event being documented, and verify that the place and date have been transcribed correctly.'
            )}
          </Paragraph>
          <ul>
            <li>
              {t('family-review.welcome-overlay.step3-item', 'The primary event is the reason the record was created.')}
            </li>
            <li>
              {t(
                'family-review.welcome-overlay.step3-item2',
                'In a birth certificate, for example, the primary event is the birth.'
              )}
            </li>
          </ul>
          {t(
            'family-review.welcome-overlay.step3-content2',
            'Historical records often contain information about other life events, and depending on the type of record, you may be asked to verify these as well. These are called inferred events. An example of an inferred event might be a birth date listed on a marriage certificate.'
          )}
        </>
      ),
    },
    {
      key: 'step4',
      headerText: t('family-review.welcome-overlay.step4', 'Step 4: Review and Submit'),
      content: (
        <Paragraph size="sm">
          <Trans i18nKey="family-review.welcome-overlay.step4-content">
            Before clicking <strong>Submit</strong>, take a moment to review your work. Double-check your corrections
            and look for any errors you may have missed. This is one final opportunity to ensure the accuracy of the
            transcription.
          </Trans>
        </Paragraph>
      ),
    },
  ]

  const faqs = [
    {
      key: 'question1',
      headerText: t(
        'family-review.welcome-overlay.faq-question1',
        'Why does FamilySearch use artificial intelligence to transcribe records?'
      ),
      content: (
        <Paragraph size="sm">
          {t(
            'family-review.welcome-overlay.faq-answer1',
            'It would take human volunteers many years to transcribe the historical records FamilySearch currently has access to. We are using artificial intelligence to accelerate this process and make more records available to more people.'
          )}
        </Paragraph>
      ),
    },
    {
      key: 'question2',
      headerText: t('family-review.welcome-overlay.faq-question2', 'How does Indexing Review help?'),
      content: (
        <>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.faq-answer2',
              'Artificial intelligence can transcribe much faster than a human, but it still makes mistakes and needs a human to review the information before it becomes searchable. This is what you do in Indexing Review.'
            )}
          </Paragraph>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.faq-answer2-p2',
              'The activity is called Indexing Review because you are paying close attention to all of the family members mentioned in the record and trying to document their relationships.'
            )}
          </Paragraph>
        </>
      ),
    },
    {
      key: 'question3',
      headerText: t(
        'family-review.welcome-overlay.faq-question3',
        'Does Indexing Review attach the historical records to the FamilySearch Family Tree?'
      ),
      content: (
        <Paragraph size="sm">
          {t(
            'family-review.welcome-overlay.faq-answer3',
            'No, it just makes the name on the record easier to find. But the person who searches for and finds the record could then add the name to Family Tree.'
          )}
        </Paragraph>
      ),
    },
    {
      key: 'question4',
      headerText: t('family-review.welcome-overlay.faq-question4', "What's the white line on images?"),
      content: (
        <Paragraph size="sm">
          {t(
            'family-review.welcome-overlay.faq-answer4',
            'Some images contain more than one historical record. The white lines show the portion of the image that the artificial intelligence identified and transcribed as a single record.'
          )}
        </Paragraph>
      ),
    },
    {
      key: 'question5',
      headerText: t(
        'family-review.welcome-overlay.faq-question5',
        'Where does all of the information for this activity come from?'
      ),
      content: (
        <Paragraph size="sm">
          {t(
            'family-review.welcome-overlay.faq-answer5',
            'The information that you review for this activity comes from historical records that were transcribed by artificial intelligence. The artificial intelligence can transcribe much faster than human volunteers, but it still makes mistakes. Your review makes the names on the record easier for other people to find.'
          )}
        </Paragraph>
      ),
    },
    {
      key: 'question6',
      headerText: t(
        'family-review.welcome-overlay.faq-question6',
        'Can I choose the historical records that I want to review?'
      ),
      content: (
        <Paragraph size="sm">
          {t(
            'family-review.welcome-overlay.faq-answer6',
            'As with most of the activities on Get Involved, you can choose the general location for where the records come from. Once the location is selected, however, the records are randomly assigned to you.'
          )}
        </Paragraph>
      ),
    },
    {
      key: 'question7',
      headerText: t('family-review.welcome-overlay.faq-question7', 'Can I save a record and work on it later?'),
      content: (
        <Paragraph size="sm">
          {t(
            'family-review.welcome-overlay.faq-answer7',
            'This is an option in traditional indexing, but it is not currently available on Get Involved. If you do not finish a review assignment, it is assigned to another volunteer.'
          )}
        </Paragraph>
      ),
    },
    {
      key: 'question8',
      headerText: t(
        'family-review.welcome-overlay.faq-question8',
        'Why should I participate in Indexing Review? What is my impact?'
      ),
      content: (
        <>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.faq-answer8',
              'By participating in Indexing Review, you are making it easier for people who visit the FamilySearch website to find their relatives, learn their family story, and connect their families.'
            )}
          </Paragraph>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.faq-answer8-p2',
              'In addition, any corrections or edits you make to the transcription help to improve the artificial intelligence so that future transcriptions are more accurate.'
            )}
          </Paragraph>
        </>
      ),
    },
    {
      key: 'question9',
      headerText: t(
        'family-review.welcome-overlay.faq-question9',
        'After I review one family, will I get the next record in the record collection?'
      ),
      content: (
        <>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.faq-answer9',
              "It is possible but not guaranteed. It just depends on what's available and how many people are volunteering on Get Involved at any given moment."
            )}
          </Paragraph>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.faq-answer9-p2',
              'If you’re interested in viewing the record that comes before or after, go to the Record Details page, and select the option for viewing the record.'
            )}
          </Paragraph>
        </>
      ),
    },
  ]

  return (
    <DialogOverlay {...welcomeOverlay} title={t('family-review.welcome-overlay.title', 'Welcome to Indexing Review')}>
      <Tabs fullWidth>
        <Tab title={t('family-review.welcome-overlay.instructions-tab', 'Instructions')}>
          {!accessFromPrison && isReady && (
            <SocialMenu
              sharable={sharableInstructions}
              buttonText={t('family-review.welcome-overlay.share-instructions.btn', 'Share Instructions')}
            />
          )}
          <Separator size="xs" />
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.ai-generated-transcription',
              'The transcription you will review for this activity was generated by AI. Your task is to compare the transcription with the image and correct any errors you find.'
            )}
          </Paragraph>
          <Paragraph size="sm">
            {t(
              'family-review.welcome-overlay.overview',
              'The instructions in the sidebar will guide you through the review. The following is an overview of the entire activity.'
            )}
          </Paragraph>
          <Separator size="sm" />
          {steps.map((step) => (
            <StyledExpander {...step} />
          ))}
        </Tab>

        <Tab title={t('family-review.welcome-overlay.faq-tab', 'FAQ')}>
          {!accessFromPrison && isReady && (
            <>
              <SocialMenu
                sharable={sharableFaq}
                buttonText={t('family-review.welcome-overlay.share-faq.btn', 'Share FAQ')}
              />
              <Separator size="xs" />
              <Paragraph size="sm">
                <Trans i18nKey="family-review.welcome-overlay.faq-content">
                  Here are some of the more common questions we receive about Indexing Review. For more personalized
                  help, please contact{' '}
                  <Link to="https://www.familysearch.org/en/fieldops/familysearch-support-contact-us">
                    FamilySearch Support
                  </Link>
                  .
                </Trans>
              </Paragraph>
            </>
          )}
          {faqs.map((faq) => (
            <StyledExpander {...faq} />
          ))}
        </Tab>
      </Tabs>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button
            onClick={() => welcomeOverlay.close()}
            emphasis="high"
            data-testid="family-review-welcome-overlay-close"
          >
            {t('family-review.welcome-overlay.done.btn', 'Done')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
